// Atendimento ao lead time — lógica compartilhada entre a aba "Pedidos"
// (histórico) e o dashboard de Compras.
//
// "status de atendimento do lead time" = situação de cada pedido frente à
// data prevista de entrega (previsão).
// "índice de atendimento ao lead time" = % de pedidos entregues na data
// prevista (ou antes). Meta = 95%.

/** Meta de atendimento ao lead time: 95% dos pedidos entregues na data. */
export const LEAD_TIME_TARGET = 0.95

export type LeadTimeStatus =
  | 'no_prazo'  // recebido na data prevista (ou antes)
  | 'atrasado'  // recebido após a data prevista
  | 'em_atraso' // ainda não recebido e a previsão já venceu
  | 'aguardando'// ainda não recebido, dentro do prazo
  | 'na'        // sem dados para avaliar (sem previsão, cancelado, etc.)

export interface LeadTimeOrderInput {
  status: string
  estimated_arrival: string | null
  received_at: string | null
  sent_at?: string | null
  lead_time_days?: number | null
}

const DAY_MS = 86_400_000

/** Início do dia (00:00 local) em ms — para comparar por data de calendário. */
function dayStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/**
 * Data prevista de entrega: usa `estimated_arrival`; se ausente, deriva de
 * `sent_at + lead_time_days` (lead time do fornecedor).
 */
export function resolveExpectedArrival(o: LeadTimeOrderInput): Date | null {
  if (o.estimated_arrival) return new Date(o.estimated_arrival)
  if (o.sent_at && o.lead_time_days) {
    return new Date(new Date(o.sent_at).getTime() + o.lead_time_days * DAY_MS)
  }
  return null
}

/** Status de atendimento ao lead time de um pedido. */
export function getLeadTimeStatus(o: LeadTimeOrderInput, now: number = Date.now()): LeadTimeStatus {
  if (o.status === 'Cancelado') return 'na'

  const expected = resolveExpectedArrival(o)
  if (!expected) return 'na'

  if (o.status === 'Recebido') {
    if (!o.received_at) return 'na'
    return dayStart(new Date(o.received_at)) <= dayStart(expected) ? 'no_prazo' : 'atrasado'
  }

  // Ainda não recebido (Pendente / Enviado)
  return expected.getTime() < now ? 'em_atraso' : 'aguardando'
}

export interface LeadTimeIndex {
  /** Fração de pedidos no prazo (0–1), ou null se não há pedidos avaliáveis. */
  index: number | null
  /** Pedidos recebidos no prazo. */
  onTime: number
  /** Pedidos recebidos avaliados (no prazo + atrasados). */
  evaluated: number
}

/**
 * Índice de atendimento ao lead time: % de pedidos recebidos entregues na
 * data prevista. Considera apenas pedidos já recebidos com previsão definida.
 */
export function leadTimeFulfillmentIndex(orders: LeadTimeOrderInput[], now: number = Date.now()): LeadTimeIndex {
  let onTime = 0
  let evaluated = 0
  for (const o of orders) {
    const s = getLeadTimeStatus(o, now)
    if (s === 'no_prazo') { onTime++; evaluated++ }
    else if (s === 'atrasado') { evaluated++ }
  }
  return { index: evaluated > 0 ? onTime / evaluated : null, onTime, evaluated }
}

export const LEAD_TIME_LABEL: Record<LeadTimeStatus, string> = {
  no_prazo:  'No prazo',
  atrasado:  'Atrasado',
  em_atraso: 'Em atraso',
  aguardando:'Aguardando',
  na:        '—',
}

export const LEAD_TIME_BADGE: Record<LeadTimeStatus, string> = {
  no_prazo:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  atrasado:  'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  em_atraso: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  aguardando:'bg-sunken text-ink-muted',
  na:        'bg-sunken text-ink-faint',
}
